import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../components/Button";
import Navbar from "../components/Navbar"; // reuse your navbar with back
import CreditHistoryItem from "../components/CreditHistoryItem";
import SupportChip from "../components/SupportChip";
import { getUserProfile, getCreditHistory } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

export default function CreditsScreen() {
    const navigate = useNavigate();
    const { user } = useAuth();

    // State management
    const [userCredits, setUserCredits] = useState(0);
    const [creditHistory, setCreditHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Fetch user profile and credit history
    useEffect(() => {
        const fetchCreditData = async () => {
            if (!user?.id) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const [profileResponse, creditHistoryResponse] = await Promise.all([
                    getUserProfile(user.id),
                    getCreditHistory()
                ]);

                // Set user credits
                setUserCredits(profileResponse.user.credits || 0);

                // Format credit history from API
                const formatCreditHistory = (transactions) => {
                    return transactions.map(transaction => {
                        const date = new Date(transaction.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                        });

                        return {
                            date,
                            description: transaction.description,
                            change: transaction.amount,
                            changeType: transaction.amount > 0 ? "increase" : "decrease"
                        };
                    });
                };

                const history = formatCreditHistory(creditHistoryResponse.transactions || []);
                setCreditHistory(history);

            } catch (error) {
                console.error('Error fetching credit data:', error);
                setError('Failed to load credit information');
            } finally {
                setLoading(false);
            }
        };

        fetchCreditData();
    }, [user?.id]);

    return (
        <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="w-full min-h-[100dvh]">
                {/* Header / hero */}
                <header className="relative w-full bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)] text-white">
                    <Navbar
                        onBack={() => navigate(-1)}
                        rightSlot={<SupportChip onClick={() => navigate("/support")} />}
                    // transparent navbar is default per your component
                    />

                    <div className="px-5 pb-6 pt-4">
                        <div className="text-center">
                            <div className="text-[12px] tracking-wide text-[#B0B0B0] font-extrabold">
                                YOUR CREDITS REMAINING
                            </div>

                            <div className="mt-4 flex items-end justify-center gap-2">
                                {/* Lightning icon */}
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M9 21L15 13H11L13 3L5 13H9V21Z"
                                        fill="#F4C505"
                                        stroke="#F4C505"
                                        strokeWidth="1"
                                    />
                                </svg>
                                <div className="text-white italic font-extrabold text-[48px] leading-none">
                                    {loading ? "..." : userCredits}
                                </div>
                            </div>

                            <div className="mt-5">
                                <PrimaryButton
                                    label="BUY CREDITS"
                                    onClick={() => navigate("/buy-credits")}
                                    className="!w-full"
                                />
                            </div>
                        </div>
                    </div>
                </header>

                {/* List */}
                <main className="w-full">
                    <div className="px-5 py-4 pb-20">
                        <div className="text-[12px] tracking-[0.05em] font-extrabold text-[#8B8B8B] mb-3">
              // CREDIT HISTORY
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading credit history...</p>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="text-center py-8">
                                <p className="text-red-600 mb-4">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="text-[#D62422] font-semibold"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : creditHistory.length > 0 ? (
                            <div className="space-y-3">
                                {creditHistory.map((item, i) => (
                                    <CreditHistoryItem
                                        key={`${item.date}-${i}`}
                                        date={item.date}
                                        description={item.description}
                                        change={item.change}
                                        changeType={item.changeType}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No credit history available</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
